import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Typography, Input, Select, Button, Tag, Modal, Checkbox, Space, Spin, Empty, message } from "antd";
import { SearchOutlined, SendOutlined, EyeOutlined, PrinterOutlined, GlobalOutlined } from "@ant-design/icons";
import styled from "styled-components";

import { WHITE, GRAY_50, GRAY_200 } from "../../constant/colors";
import {
  getSendableEducationContentAPI,
  searchHealthGovAdminAPI,
  sendKnowledgeCenterToPatientAPI
} from "../../api/api";

const { Title, Text, Paragraph } = Typography;

const PageContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 24px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  min-height: calc(100vh - 100px);
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const ContentCard = styled.div`
  border: 1px solid ${GRAY_200};
  border-radius: 12px;
  overflow: hidden;
  background: ${WHITE};
  transition: box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const KnowledgeCenter = () => {
  const location = useLocation();
  const preSelectedPatientId = location.state?.patientId;
  const preSelectedPatientName = location.state?.patientName;

  const [sendableContent, setSendableContent] = useState([]);
  const [healthGovResults, setHealthGovResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [drugFilter, setDrugFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [healthGovLoading, setHealthGovLoading] = useState(false);

  const [previewContent, setPreviewContent] = useState(null);
  const [sendModalContent, setSendModalContent] = useState(null);
  const [patientIdsInput, setPatientIdsInput] = useState(preSelectedPatientId ? String(preSelectedPatientId) : "");
  const [selectedChannels, setSelectedChannels] = useState({
    email: true,
    sms: false,
    inApp: true
  });
  const [sending, setSending] = useState(false);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (drugFilter) params.drugName = drugFilter;
      const { data } = await getSendableEducationContentAPI(params);
      setSendableContent(Array.isArray(data) ? data : []);
    } catch {
      setSendableContent([]);
    } finally {
      setLoading(false);
    }
  }, [drugFilter]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Health.gov search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 3) {
      setHealthGovResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setHealthGovLoading(true);
      try {
        const { data } = await searchHealthGovAdminAPI(searchQuery);
        setHealthGovResults(Array.isArray(data) ? data : []);
      } catch {
        setHealthGovResults([]);
      } finally {
        setHealthGovLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredContent = sendableContent.filter((c) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (c.title || "").toLowerCase().includes(q) ||
      (c.description || "").toLowerCase().includes(q) ||
      (c.drugName || "").toLowerCase().includes(q) ||
      (c.category || "").toLowerCase().includes(q)
    );
  });

  const drugNames = [...new Set(sendableContent.map((c) => c.drugName).filter(Boolean))];

  const handleSend = async () => {
    if (!sendModalContent) return;
    const ids = patientIdsInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (ids.length === 0) {
      message.warning("Please enter at least one patient ID");
      return;
    }
    if (!selectedChannels.email && !selectedChannels.sms && !selectedChannels.inApp) {
      message.warning("Please select at least one delivery method");
      return;
    }
    setSending(true);
    try {
      const isHG = !!sendModalContent.accessibleVersion;
      await sendKnowledgeCenterToPatientAPI(sendModalContent.id, {
        patientIds: ids.map(Number),
        channels: selectedChannels,
        contentType: isHG ? "health_gov" : "targeted_education",
        contentTitle: sendModalContent.title,
        contentUrl: isHG ? sendModalContent.accessibleVersion : sendModalContent.linkUrl,
        contentDescription: sendModalContent.description,
        contentImageUrl: isHG ? sendModalContent.imageUrl : sendModalContent.coverImageUrl
      });
      message.success(`Content sent to ${ids.length} patient(s)`);
      setSendModalContent(null);
    } catch {
      message.error("Failed to send content");
    } finally {
      setSending(false);
    }
  };

  const renderCard = (item, isHealthGov = false) => (
    <ContentCard key={isHealthGov ? `hg-${item.id}` : `ec-${item.id}`}>
      <div
        style={{
          height: 160,
          background: (isHealthGov ? item.imageUrl : item.coverImageUrl)
            ? `url(${isHealthGov ? item.imageUrl : item.coverImageUrl}) center/cover no-repeat`
            : "linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {!(isHealthGov ? item.imageUrl : item.coverImageUrl) && (
          <GlobalOutlined style={{ fontSize: 40, color: "#91d5ff" }} />
        )}
      </div>
      <div style={{ padding: 16, flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ marginBottom: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {item.category && (
            <Tag color="blue" style={{ borderRadius: 10, fontSize: 11 }}>
              {item.category}
            </Tag>
          )}
          {item.drugName && (
            <Tag color="green" style={{ borderRadius: 10, fontSize: 11 }}>
              {item.drugName}
            </Tag>
          )}
          {isHealthGov && (
            <Tag color="cyan" style={{ borderRadius: 10, fontSize: 11 }}>
              Health.gov
            </Tag>
          )}
        </div>
        <Text strong style={{ fontSize: 15, display: "block", marginBottom: 4, lineHeight: 1.3 }}>
          {item.title}
        </Text>
        <Paragraph ellipsis={{ rows: 3 }} style={{ fontSize: 13, color: "#696662", marginBottom: 12, flex: 1 }}>
          {item.description || ""}
        </Paragraph>
        <Space wrap>
          <Button
            type="primary"
            size="small"
            icon={<SendOutlined />}
            style={{ borderRadius: 6, background: "#52c41a", borderColor: "#52c41a" }}
            onClick={() => setSendModalContent(item)}
          >
            Send to Patient
          </Button>
          <Button
            size="small"
            icon={<EyeOutlined />}
            style={{ borderRadius: 6 }}
            onClick={() => setPreviewContent(item)}
          >
            Preview
          </Button>
          <Button
            size="small"
            icon={<PrinterOutlined />}
            style={{ borderRadius: 6 }}
            onClick={() => {
              const url = isHealthGov ? item.accessibleVersion : item.linkUrl;
              if (url) window.open(url, "_blank", "noopener,noreferrer");
            }}
          >
            Print
          </Button>
        </Space>
      </div>
    </ContentCard>
  );

  const channelCard = (key, icon, label, desc, isSelected) => (
    <div
      style={{
        border: `1px solid ${isSelected ? "#1890ff" : "#d9d9d9"}`,
        borderRadius: 10,
        padding: "10px 14px",
        cursor: "pointer",
        background: isSelected ? "#e6f7ff" : "#fff"
      }}
      onClick={() => setSelectedChannels((c) => ({ ...c, [key]: !c[key] }))}
    >
      <Checkbox checked={isSelected} style={{ marginRight: 8 }} />
      <Text strong>
        {icon} {label}
      </Text>
      {key === "inApp" && (
        <Tag color="green" style={{ marginLeft: 6, borderRadius: 10, fontSize: 10 }}>
          Recommended
        </Tag>
      )}
      <Text style={{ display: "block", fontSize: 12, color: "#696662", marginLeft: 28 }}>{desc}</Text>
    </div>
  );

  return (
    <PageContainer>
      <Title level={2} style={{ margin: 0 }}>
        Patient Content Center
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
        Search and ePrescribe educational content to send directly to your patients
      </Text>

      <div
        style={{
          background: "#f0faf5",
          border: "1px solid #b7eb8f",
          borderRadius: 10,
          padding: "12px 16px",
          marginBottom: 20,
          display: "flex",
          alignItems: "center",
          gap: 10
        }}
      >
        <SendOutlined style={{ fontSize: 18, color: "#52c41a" }} />
        <div>
          <Text strong>ePrescribe Patient Education</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Select content below to send directly to patients via email, SMS, or through TailorMed Connect. Health.gov
            resources are marked with a badge.
          </Text>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          placeholder="Search by drug name, topic, or content type..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          style={{ flex: 1, borderRadius: 8, height: 40 }}
        />
        <Select
          placeholder="All Drugs"
          allowClear
          value={drugFilter}
          onChange={setDrugFilter}
          style={{ width: 180 }}
          options={drugNames.map((d) => ({ value: d, label: d }))}
        />
      </div>

      {preSelectedPatientId && (
        <div
          style={{
            background: "#e6f7ff",
            border: "1px solid #91d5ff",
            borderRadius: 8,
            padding: "8px 14px",
            marginBottom: 16,
            fontSize: 13
          }}
        >
          📋 Sending to: <strong>{preSelectedPatientName || `Patient #${preSelectedPatientId}`}</strong>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {filteredContent.length > 0 && (
            <>
              <Text strong style={{ fontSize: 16, display: "block", marginTop: 8 }}>
                📚 Education Content ({filteredContent.length})
              </Text>
              <CardGrid>{filteredContent.map((c) => renderCard(c, false))}</CardGrid>
            </>
          )}
          {filteredContent.length === 0 && !searchQuery && (
            <Empty
              description='No content marked as sendable yet. Toggle "Patient Content Center" on education items in Settings.'
              style={{ marginTop: 40 }}
            />
          )}
          {searchQuery && searchQuery.length >= 3 && (
            <>
              <Text strong style={{ fontSize: 16, display: "block", marginTop: 24 }}>
                🏥 Health.gov Resources {healthGovLoading && <Spin size="small" style={{ marginLeft: 8 }} />}
              </Text>
              {healthGovResults.length > 0 ? (
                <CardGrid>{healthGovResults.map((r) => renderCard(r, true))}</CardGrid>
              ) : (
                !healthGovLoading && (
                  <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                    No Health.gov results for &ldquo;{searchQuery}&rdquo;
                  </Text>
                )
              )}
            </>
          )}
        </>
      )}

      {/* Preview Modal */}
      <Modal
        title="Patient Portal Preview"
        open={!!previewContent}
        onCancel={() => setPreviewContent(null)}
        footer={null}
        width={480}
      >
        {previewContent && (
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
              How patients will see this content on their mobile device
            </Text>
            <div
              style={{
                border: "2px solid #d9d9d9",
                borderRadius: 24,
                overflow: "hidden",
                maxWidth: 320,
                margin: "0 auto"
              }}
            >
              <div style={{ background: "#fff", padding: "12px 16px", borderBottom: "1px solid #eee" }}>
                <Text strong style={{ fontSize: 13 }}>
                  ← Back to Portal
                </Text>
              </div>
              <div style={{ padding: 16, background: "#fff" }}>
                <Text type="secondary" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  MEDICATIONS
                </Text>
                <Text style={{ display: "block", fontSize: 11, color: "#696662", marginBottom: 12 }}>
                  Educational resources for your prescribed medications
                </Text>
                {(previewContent.coverImageUrl || previewContent.imageUrl) && (
                  <img
                    src={previewContent.coverImageUrl || previewContent.imageUrl}
                    alt=""
                    style={{ width: "100%", borderRadius: 10, marginBottom: 12 }}
                  />
                )}
                {previewContent.category && (
                  <Tag color="blue" style={{ marginBottom: 6, borderRadius: 10, fontSize: 10 }}>
                    {previewContent.category}
                  </Tag>
                )}
                <Text strong style={{ fontSize: 15, display: "block", marginBottom: 4 }}>
                  {previewContent.title}
                </Text>
                <Text style={{ fontSize: 12, color: "#696662", display: "block", marginBottom: 12 }}>
                  {(previewContent.description || "").slice(0, 200)}
                </Text>
                <Button type="primary" block style={{ borderRadius: 8 }}>
                  Learn more →
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Send to Patient Modal */}
      <Modal
        title="Send to Patient"
        open={!!sendModalContent}
        onCancel={() => setSendModalContent(null)}
        footer={null}
        width={820}
      >
        {sendModalContent && (
          <div style={{ display: "flex", gap: 24 }}>
            {/* Left side — delivery options */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  padding: 12,
                  background: GRAY_50,
                  borderRadius: 10,
                  marginBottom: 20
                }}
              >
                {(sendModalContent.coverImageUrl || sendModalContent.imageUrl) && (
                  <img
                    src={sendModalContent.coverImageUrl || sendModalContent.imageUrl}
                    alt=""
                    style={{ width: 64, height: 64, borderRadius: 8, objectFit: "cover" }}
                  />
                )}
                <div>
                  <Text strong style={{ fontSize: 14 }}>
                    {sendModalContent.title}
                  </Text>
                  {sendModalContent.category && (
                    <Text style={{ display: "block", fontSize: 12, color: "#696662" }}>
                      {sendModalContent.category}
                    </Text>
                  )}
                </div>
              </div>

              <Text strong style={{ display: "block", marginBottom: 10 }}>
                Select delivery method:
              </Text>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                {channelCard(
                  "email",
                  "📧",
                  "Email",
                  "Send a responsive email with content link",
                  selectedChannels.email
                )}
                {channelCard(
                  "sms",
                  "📱",
                  "SMS Text",
                  "Send text message with mobile-optimized link",
                  selectedChannels.sms
                )}
                {channelCard(
                  "inApp",
                  "💬",
                  "TailorMed Connect",
                  "Available immediately in patient's portal",
                  selectedChannels.inApp
                )}
              </div>

              {!preSelectedPatientId && (
                <div style={{ marginBottom: 20 }}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>
                    Patient ID(s)
                  </Text>
                  <Input
                    placeholder="Enter patient IDs (comma-separated)"
                    value={patientIdsInput}
                    onChange={(e) => setPatientIdsInput(e.target.value)}
                    style={{ borderRadius: 8 }}
                  />
                </div>
              )}

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <Button onClick={() => setSendModalContent(null)} style={{ borderRadius: 8 }}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={sending}
                  onClick={handleSend}
                  style={{ borderRadius: 8, background: "#52c41a", borderColor: "#52c41a" }}
                >
                  Send to Patient
                </Button>
              </div>
            </div>

            {/* Right side — phone mockup preview */}
            <div style={{ width: 280, flexShrink: 0 }}>
              <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12, textAlign: "center" }}>
                Patient will see this on TailorMed Connect
              </Text>
              <div
                style={{
                  border: "8px solid #1a1a2e",
                  borderRadius: 32,
                  overflow: "hidden",
                  background: "#1a1a2e",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.18)"
                }}
              >
                {/* Phone notch */}
                <div
                  style={{
                    background: "#1a1a2e",
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <div
                    style={{
                      width: 80,
                      height: 6,
                      background: "#333",
                      borderRadius: 3
                    }}
                  />
                </div>
                {/* Phone screen — Education tab mockup */}
                <div style={{ background: "#F6F2ED", minHeight: 320 }}>
                  {/* Page title */}
                  <div style={{ padding: "12px 12px 0" }}>
                    <Text strong style={{ fontSize: 16, color: "#393735", display: "block" }}>
                      Education Center
                    </Text>
                    <Text style={{ fontSize: 9, color: "#696662" }}>
                      Learn more about your medications and treatments
                    </Text>
                  </div>
                  {/* Search bar */}
                  <div style={{ padding: "8px 12px" }}>
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #EAE5DD",
                        borderRadius: 99,
                        padding: "5px 10px",
                        fontSize: 9,
                        color: "#bbb",
                        display: "flex",
                        alignItems: "center",
                        gap: 4
                      }}
                    >
                      <SearchOutlined style={{ fontSize: 10 }} /> Search health topics...
                    </div>
                  </div>
                  {/* Selected for you section */}
                  <div style={{ padding: "0 12px" }}>
                    <Text strong style={{ fontSize: 10, color: "#393735", display: "block", marginBottom: 6 }}>
                      Selected for you
                    </Text>
                    {/* The sent content card */}
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #CDE6EC",
                        borderRadius: 10,
                        padding: 8,
                        display: "flex",
                        gap: 6,
                        marginBottom: 8
                      }}
                    >
                      <div
                        style={{
                          width: 52,
                          height: 52,
                          borderRadius: 8,
                          overflow: "hidden",
                          flexShrink: 0,
                          background: "#f5f5f5"
                        }}
                      >
                        {sendModalContent.coverImageUrl || sendModalContent.imageUrl ? (
                          <img
                            src={sendModalContent.coverImageUrl || sendModalContent.imageUrl}
                            alt=""
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "#EBF9FD"
                            }}
                          >
                            <GlobalOutlined style={{ fontSize: 16, color: "#389BB4" }} />
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ fontSize: 11, color: "#393735", display: "block", lineHeight: 1.3 }}>
                          {sendModalContent.title}
                        </Text>
                        <Text
                          style={{
                            fontSize: 9,
                            color: "#696662",
                            display: "block",
                            lineHeight: 1.3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {(sendModalContent.description || "").slice(0, 60)}
                        </Text>
                        <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
                          {sendModalContent.drugName && (
                            <div
                              style={{
                                background: "#EBF9FD",
                                border: "1px solid #CDE6EC",
                                borderRadius: 99,
                                padding: "1px 6px",
                                fontSize: 8,
                                fontWeight: 700,
                                color: "#389BB4"
                              }}
                            >
                              {sendModalContent.drugName}
                            </div>
                          )}
                          {sendModalContent.category && (
                            <div
                              style={{
                                background: "#EBF9FD",
                                border: "1px solid #CDE6EC",
                                borderRadius: 99,
                                padding: "1px 6px",
                                fontSize: 8,
                                fontWeight: 700,
                                color: "#389BB4"
                              }}
                            >
                              {sendModalContent.category}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ color: "#8F8C86", fontSize: 10, flexShrink: 0, marginTop: 2 }}>›</div>
                    </div>
                    {/* Placeholder for other content */}
                    <Text strong style={{ fontSize: 10, color: "#393735", display: "block", marginBottom: 6 }}>
                      Education & Support
                    </Text>
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #EAE5DD",
                        borderRadius: 10,
                        padding: 8,
                        display: "flex",
                        gap: 6,
                        marginBottom: 6,
                        opacity: 0.5
                      }}
                    >
                      <div
                        style={{
                          width: 52,
                          height: 52,
                          borderRadius: 8,
                          background: "#f0f0f0",
                          flexShrink: 0
                        }}
                      />
                      <div>
                        <div
                          style={{ width: 100, height: 8, background: "#e0e0e0", borderRadius: 4, marginBottom: 4 }}
                        />
                        <div style={{ width: 140, height: 6, background: "#eee", borderRadius: 3 }} />
                      </div>
                    </div>
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #EAE5DD",
                        borderRadius: 10,
                        padding: 8,
                        display: "flex",
                        gap: 6,
                        opacity: 0.5
                      }}
                    >
                      <div
                        style={{
                          width: 52,
                          height: 52,
                          borderRadius: 8,
                          background: "#f0f0f0",
                          flexShrink: 0
                        }}
                      />
                      <div>
                        <div
                          style={{ width: 80, height: 8, background: "#e0e0e0", borderRadius: 4, marginBottom: 4 }}
                        />
                        <div style={{ width: 120, height: 6, background: "#eee", borderRadius: 3 }} />
                      </div>
                    </div>
                  </div>
                  {/* Bottom tab bar */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                      borderTop: "1px solid #EAE5DD",
                      padding: "6px 0 2px",
                      marginTop: 10,
                      background: "#fff"
                    }}
                  >
                    {["Meds", "Assistance", "Wallet", "Education", "Help"].map((tab) => (
                      <div key={tab} style={{ textAlign: "center" }}>
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 9,
                            background: tab === "Education" ? "#389BB4" : "#d9d9d9",
                            margin: "0 auto 2px",
                            opacity: tab === "Education" ? 1 : 0.4
                          }}
                        />
                        <Text
                          style={{
                            fontSize: 7,
                            color: tab === "Education" ? "#389BB4" : "#999",
                            fontWeight: tab === "Education" ? 700 : 400
                          }}
                        >
                          {tab}
                        </Text>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Phone bottom bar */}
                <div
                  style={{
                    background: "#1a1a2e",
                    height: 16,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <div
                    style={{
                      width: 60,
                      height: 4,
                      background: "#555",
                      borderRadius: 2
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </PageContainer>
  );
};

export { KnowledgeCenter };
